import React, { useEffect, useRef, useState } from "react";
import { Download, X, Apple, Share2 } from "lucide-react";

/**
 * Smart install prompt for PWA.
 *
 * Rules:
 *  - Renders only after 2nd visit (counts mounts in this session-cluster via localStorage).
 *  - Not shown if already in standalone mode (already installed).
 *  - User can dismiss → snoozed for 30 days.
 *  - Chrome/Edge use the deferred beforeinstallprompt event; iOS Safari shows manual hint.
 */
const VISITS_KEY = "carhive_pwa_visits_v1";
const DISMISSED_KEY = "carhive_pwa_dismissed_until_v1";
const COOLDOWN_DAYS = 30;

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIosSafari() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = /WebKit/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return iOS && isSafari;
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);
  const visitedRef = useRef(false);

  // Track visits + decide eligibility on mount
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (visitedRef.current) return; // StrictMode-safe single fire
    visitedRef.current = true;
    if (isStandalone()) return; // already installed
    try {
      // Snoozed?
      const until = parseInt(localStorage.getItem(DISMISSED_KEY) || "0", 10);
      if (until && Date.now() < until) return;
      // Increment visit counter (deduped: only count once per page load)
      const count = parseInt(localStorage.getItem(VISITS_KEY) || "0", 10);
      const next = count + 1;
      localStorage.setItem(VISITS_KEY, String(next));
      // Show only from 2nd visit onwards
      if (next < 2) return;
      // iOS Safari: no beforeinstallprompt → manual hint card
      if (isIosSafari()) {
        setIosHint(true);
        setVisible(true);
        return;
      }
    } catch { /* ignore */ }
  }, []);

  // Listen for Chrome/Edge prompt event
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isStandalone()) return;
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Honor visit-count rule
      try {
        const count = parseInt(localStorage.getItem(VISITS_KEY) || "0", 10);
        const until = parseInt(localStorage.getItem(DISMISSED_KEY) || "0", 10);
        if (count >= 2 && (!until || Date.now() >= until)) {
          setVisible(true);
        }
      } catch { /* ignore */ }
    };
    window.addEventListener("beforeinstallprompt", handler);
    const installed = () => { setVisible(false); setDeferredPrompt(null); };
    window.addEventListener("appinstalled", installed);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installed);
    };
  }, []);

  const onInstall = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "dismissed") snooze();
    } catch { /* ignore */ }
    setDeferredPrompt(null);
    setVisible(false);
  };

  const snooze = () => {
    try {
      const until = Date.now() + COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
      localStorage.setItem(DISMISSED_KEY, String(until));
    } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;
  // iOS path: no install button — show step-by-step
  if (iosHint) {
    return (
      <div
        className="fixed inset-x-3 z-[60] mx-auto max-w-md rounded-2xl border border-zinc-700 bg-zinc-950/95 p-4 text-white shadow-2xl backdrop-blur-md sm:right-6 sm:left-auto"
        style={{ bottom: "max(env(safe-area-inset-bottom, 0px), 0.75rem)" }}
        data-testid="pwa-install-prompt-ios"
      >
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-600/15 text-red-400">
            <Apple className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">Zum Home-Bildschirm hinzufügen</p>
            <p className="mt-1 text-xs leading-relaxed text-zinc-400">
              Tippe in Safari auf <Share2 className="mx-1 inline h-3.5 w-3.5 align-text-bottom text-zinc-300" />
              <span className="font-semibold text-zinc-200">Teilen</span> &rarr; <span className="font-semibold text-zinc-200">„Zum Home-Bildschirm"</span>, um CarHive wie eine App zu öffnen.
            </p>
          </div>
          <button
            type="button"
            onClick={snooze}
            className="shrink-0 rounded-md p-1 text-zinc-500 hover:bg-zinc-800 hover:text-white"
            title="Verbergen"
            data-testid="pwa-dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }
  // Chrome/Edge path: native install
  return (
    <div
      className="fixed inset-x-3 z-[60] mx-auto max-w-md rounded-2xl border border-zinc-700 bg-zinc-950/95 p-4 text-white shadow-2xl backdrop-blur-md sm:right-6 sm:left-auto"
      style={{ bottom: "max(env(safe-area-inset-bottom, 0px), 0.75rem)" }}
      data-testid="pwa-install-prompt"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-600/15 text-red-400">
          <Download className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">CarHive als App installieren</p>
          <p className="mt-0.5 text-xs text-zinc-400">Schneller Zugriff, kein Browser-Bar, eigene App-Icon.</p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={onInstall}
              className="flex-1 rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-500"
              data-testid="pwa-install-button"
            >
              Installieren
            </button>
            <button
              type="button"
              onClick={snooze}
              className="rounded-lg border border-zinc-700 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-900"
              data-testid="pwa-dismiss"
            >
              Später
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
